import { TranslatePipe } from "@ngx-translate/core";
import { Apollo } from "apollo-angular";
import { filter, firstValueFrom, map } from "rxjs";

import { ChangeDetectionStrategy, Component, computed, inject, resource, signal } from "@angular/core";
import { MatSortModule, Sort } from "@angular/material/sort";

import {
  CustomerWithMinimalImpactNotificationQueryQuery,
  SubscriptionType,
} from "../../apis/networkdashboard/graphql/graphql";
import { CUSTOMER_WITH_MINIMAL_IMPACT_NOTIFICATIONS_QUERY } from "../../apis/networkdashboard/static/graphql-queries/customer";
import { FooterComponent } from "../../components/common/footer/footer";
import { LoaderComponent } from "../../components/common/loader/loader";
import { NavigationComponent } from "../../components/common/navigation/navigation";
import { AuthService } from "../../services/authservice";
import { NotificationSettingsRowComponent } from "./notification-settings-row";

function compareStrings(a: string, b: string, isAsc: boolean) {
  return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}

const sortKey: Partial<Record<string, (s: SubscriptionType) => string>> = {
  description: (s) => s.description,
  presentableName: (s) => s.customerDescription ?? "-",
  type: (s) => s.product.type,
};

@Component({
  selector: "page-notification-settings",
  templateUrl: "notification-settings.html",
  styleUrl: "notification-settings.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FooterComponent,
    LoaderComponent,
    MatSortModule,
    NavigationComponent,
    NotificationSettingsRowComponent,
    TranslatePipe,
  ],
})
export class NotificationSettingsPage {
  private apollo = inject(Apollo);
  private auth = inject(AuthService);

  private _subscriptionData = resource({
    params: () => ({ customerId: this.auth.viewingCustomer().customerId }),
    loader: ({ params }) =>
      firstValueFrom(
        this.apollo
          .watchQuery<CustomerWithMinimalImpactNotificationQueryQuery>({
            query: CUSTOMER_WITH_MINIMAL_IMPACT_NOTIFICATIONS_QUERY,
            fetchPolicy: "network-only",
            variables: {
              customerId: params.customerId,
              products: ["IP", "FW", "L2VPN", "L3VPN", "LightPath", "Port"],
            },
          })
          .valueChanges.pipe(
            filter(({ loading }) => !loading),
            map(({ data }) => (data?.customerSubscriptions ?? []) as SubscriptionType[]),
          ),
      ),
    defaultValue: [] as SubscriptionType[],
  });

  protected subscriptions = computed(() => this._subscriptionData.value() ?? []);
  protected loading = computed(() => this._subscriptionData.isLoading());

  private sortActive = signal("description");
  private sortDirection = signal("desc");
  private sortAscending = computed(() => this.sortDirection() === "asc");

  protected sortedData = computed(() => {
    const subscriptions = this.subscriptions().slice();
    const key = sortKey[this.sortActive()];
    const isAsc = this.sortAscending();
    return key ? subscriptions.sort((a, b) => compareStrings(key(a), key(b), isAsc)) : subscriptions;
  });

  sortData(sort: Sort) {
    this.sortActive.set(sort.active);
    this.sortDirection.set(sort.direction);
  }
}
